import { useId } from "react";
import { cn } from "@/lib/cn";

const BLOB =
  "M30.2544 31.0306C28.2502 33.3039 24.4974 33.8527 21.8072 35.1332C18.1391 36.8805 12.3565 42.113 8.57004 38.0279C7.37828 36.742 7.35406 35.1346 6.35465 33.893C4.75802 31.9103 1.3428 31.8512 0.526322 28.7332C-0.337234 25.4323 1.84049 22.9493 5.0378 23.0044C9.02468 23.0744 12.4359 22.9372 16.4349 22.9533C19.7721 22.9668 19.807 18.1661 16.5223 18.0827C12.4022 17.9778 8.11674 18.197 3.75726 17.9441C-1.1833 17.6576 -0.193308 10.4412 3.91598 8.68042C6.43536 7.60165 9.53179 7.3407 12.2475 6.22561C16.7496 4.37609 23.8733 -1.23971 28.7103 0.760453C33.2312 2.6288 31.7354 8.47731 31.6036 12.2718C31.5471 13.8846 31.5498 15.7059 31.6036 17.32C31.7408 21.4522 33.1733 27.719 30.2558 31.0293L30.2544 31.0306Z";
const BAR_TOP =
  "M9.89765 11.2455C7.62174 11.9907 7.46705 15.1894 9.78063 15.9426C10.0026 16.0153 10.2366 16.0462 10.4693 16.0462L24.0105 16.0489C24.9467 16.0489 25.8156 15.5001 26.1653 14.6312C26.6415 13.4461 26.2581 11.8898 25.022 11.3195C24.7341 11.1863 24.4167 11.1312 24.0993 11.1312L10.585 11.1433C10.3523 11.1433 10.1196 11.1742 9.899 11.2469L9.89765 11.2455Z";
const BAR_BOTTOM =
  "M19.8473 24.918H10.5016C9.18666 24.918 8.12073 25.9839 8.12073 27.2988V27.4938C8.12073 28.8087 9.18666 29.8747 10.5016 29.8747H19.8473C21.1622 29.8747 22.2282 28.8087 22.2282 27.4938V27.2988C22.2282 25.9839 21.1622 24.918 19.8473 24.918Z";

type Variant = "color" | "dark" | "ink" | "white";

/** The symbol alone (viewBox 33 × 40). Minimum 16px high. */
export function LogoSymbol({ height = 26, variant = "color", className }: { height?: number; variant?: Variant; className?: string }) {
  const gid = useId();
  const fill = variant === "ink" ? "#1B1C31" : variant === "white" ? "#FFFFFF" : `url(#${gid})`;
  // Mono variants punch the bars out in the background colour.
  const bars = variant === "ink" ? "#FFFFFF" : variant === "white" ? "#5B4FE0" : "#FFFFFF";
  return (
    <svg width={(height * 33) / 40} height={height} viewBox="0 0 33 40" fill="none" aria-hidden className={cn("shrink-0", className)}>
      <path d={BLOB} fill={fill} />
      <path d={BAR_TOP} fill={bars} />
      <path d={BAR_BOTTOM} fill={bars} />
      {(variant === "color" || variant === "dark") && (
        <defs>
          <linearGradient id={gid} x1="23.97" y1="-0.97" x2="9.08" y2="39.93" gradientUnits="userSpaceOnUse">
            <stop offset="0.06" stopColor="#0C9FFE" />
            <stop offset="0.42" stopColor="#3426FF" />
            <stop offset="0.86" stopColor="#981EFF" />
          </linearGradient>
        </defs>
      )}
    </svg>
  );
}

/**
 * Symbol + wordmark: Inter 900, tracking −0.03em, gap = 9/19 of the word size.
 * Below 88px wide, use the symbol alone.
 */
export function Logo({ size = 19, variant = "color", className }: { size?: number; variant?: Variant; className?: string }) {
  const word = variant === "dark" || variant === "white" ? "text-white" : "text-ink";
  return (
    <span className={cn("inline-flex items-center", className)} style={{ gap: (size * 9) / 19 }} aria-label="Melrys">
      <LogoSymbol height={size * 1.37} variant={variant} />
      <span className={cn("font-black tracking-[-0.03em] leading-none", word)} style={{ fontSize: size }}>
        Melrys
      </span>
    </span>
  );
}
